import { useId, type ReactNode } from 'react';
import { cx } from './util.ts';
import './base.css';
import './DataTable.css';

export interface Column<R> {
  key: string;
  header: ReactNode;
  cell: (row: R) => ReactNode;
  /** Cifras: alineadas a la derecha en monoespaciada. */
  numeric?: boolean;
  /** La celda de esta columna encabeza la fila (th scope="row"). */
  rowHeader?: boolean;
  /** Clase extra en la cabecera y las celdas de la columna (p. ej. para ocultarla según el ancho). */
  className?: string;
}

export interface DataTableProps<R> {
  caption: ReactNode;
  /** Oculta el título visualmente (sigue siendo el nombre de la tabla). */
  hideCaption?: boolean;
  columns: readonly Column<R>[];
  rows: readonly R[];
  rowKey: (row: R) => string;
  /** Qué mostrar si no hay filas (p. ej. un EmptyState). */
  empty?: ReactNode;
  /** Fila resaltada (selección actual). */
  selectedKey?: string;
  className?: string;
}

/** Tabla sencilla y accesible; con desplazamiento horizontal enfocable si no cabe. */
export function DataTable<R>({
  caption,
  hideCaption = false,
  columns,
  rows,
  rowKey,
  empty,
  selectedKey,
  className,
}: DataTableProps<R>) {
  const captionId = useId();
  return (
    <div
      className={cx('af-table-wrap', className)}
      role="region"
      aria-labelledby={captionId}
      tabIndex={0}
    >
      <table className="af-table">
        <caption id={captionId} className={cx('af-table__caption', hideCaption && 'af-sr-only')}>
          {caption}
        </caption>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cx(col.numeric && 'af-table__num', col.className)}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="af-table__empty">
                {empty ?? 'Sin datos'}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              return (
                <tr key={key} aria-current={key === selectedKey ? 'true' : undefined}>
                  {columns.map((col) => {
                    const cls = cx(col.numeric && 'af-table__num', col.className);
                    return col.rowHeader ? (
                      <th key={col.key} scope="row" className={cls}>
                        {col.cell(row)}
                      </th>
                    ) : (
                      <td key={col.key} className={cls}>
                        {col.cell(row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
